'use client'

import classNames from 'clsx'
import { useState } from 'react'

import Image from '@/ui/MediaImage'
import { isSvgImageSrc, shouldUnoptimizeImageSrc } from '@/utils/imageUrl'

import S from './SportTypeIcon.module.scss'

export type SportTypeIconSize = 20 | 24

export interface SportTypeIconProps {
  iconUrl?: string | null
  alt?: string
  size?: SportTypeIconSize
  className?: string
  /** Keeps layout space when no icon is shown (e.g. "All sports"). */
  reserveSpace?: boolean
  /** Shows the neutral placeholder background. Disable for invisible spacers. */
  showPlaceholder?: boolean
}

function SportTypeIconContent({
  iconUrl,
  alt = '',
  size = 24,
  className,
  showPlaceholder = true
}: SportTypeIconProps) {
  const [isLoaded, setIsLoaded] = useState(false)
  const [hasError, setHasError] = useState(false)

  const canLoadImage = Boolean(iconUrl) && !hasError
  const isLoading = canLoadImage && !isLoaded
  const shouldShowPlaceholder = showPlaceholder && (!canLoadImage || isLoading)

  return (
    <span
      className={classNames(S.root, S[`size${size}`], className)}
      data-loading={isLoading || undefined}
    >
      {shouldShowPlaceholder ? (
        <span
          className={S.placeholder}
          aria-hidden
          data-testid="sport-type-icon-placeholder"
        />
      ) : null}
      {canLoadImage ? (
        <Image
          src={iconUrl!}
          alt={alt}
          width={size}
          height={size}
          className={classNames(S.image, isLoaded && S.imageVisible)}
          unoptimized={
            isSvgImageSrc(iconUrl!) || shouldUnoptimizeImageSrc(iconUrl!)
          }
          onLoad={() => setIsLoaded(true)}
          onError={() => setHasError(true)}
        />
      ) : null}
    </span>
  )
}

export function SportTypeIcon({
  reserveSpace = true,
  iconUrl,
  ...props
}: SportTypeIconProps) {
  if (!reserveSpace) {
    return null
  }

  return (
    <SportTypeIconContent
      key={iconUrl ?? 'missing'}
      iconUrl={iconUrl}
      {...props}
    />
  )
}
